import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import { useAuth } from './AuthContext';

interface Database {
  id: string;
  name: string;
  created_at: string;
  file_name: string;
  file_type: string;
  columns: any;
  row_count: number;
  is_active: boolean;
}

interface DatabaseContextType {
  databases: Database[];
  activeDatabase: Database | null;
  setActiveDatabase: (db: Database | null) => void;
  isLoading: boolean;
  error: string | null;
  fetchDatabases: () => Promise<Database[]>;
}

const DatabaseContext = createContext<DatabaseContextType | undefined>(undefined);

const STORAGE_KEY = 'activeDatabaseId';

export const DatabaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAuthChecking } = useAuth();
  const [databases, setDatabases] = useState<Database[]>([]);
  const [activeDatabase, setActiveDatabaseState] = useState<Database | null>(null);
  // Initialize loading to true if we have a persisted database ID
  // This ensures ProtectedRoute shows loading screen while we fetch and restore
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    return !!localStorage.getItem(STORAGE_KEY); // Start loading if we have a persisted ID
  });
  const [error, setError] = useState<string | null>(null);
  const hasRestoredRef = useRef<boolean>(false);

  const fetchDatabases = useCallback(async (): Promise<Database[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`${API_ROUTES.DB_MANAGER.BASE}${API_ROUTES.DB_MANAGER.LIST}`);
      const fetchedDatabases: Database[] = response.data;
      setDatabases(fetchedDatabases);
      
      // Restore active database from localStorage on initial load
      // After initial restoration, only verify that current active database still exists
      const shouldRestore = !hasRestoredRef.current;
      let restoredDatabase: Database | null = null;
      
      if (shouldRestore) {
        hasRestoredRef.current = true;
        const persistedId = localStorage.getItem(STORAGE_KEY);
        if (persistedId) {
          // Try both string and number comparison in case of type mismatch
          restoredDatabase = fetchedDatabases.find((db: Database) => 
            String(db.id) === String(persistedId) || db.id === persistedId
          ) || null;
          if (!restoredDatabase) {
            // Database was deleted or doesn't exist anymore, clear persistence
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }
      
      setActiveDatabaseState((currentActive: Database | null) => {
        if (currentActive) {
          // Database is already active, verify it still exists in the fetched list
          const stillExists = fetchedDatabases.some((db: Database) => db.id === currentActive.id);
          if (!stillExists) {
            // Current active database was deleted, clear it
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          return currentActive;
        }
        
        // No active database - restore from localStorage only on initial load
        return restoredDatabase;
      });
      
      return fetchedDatabases;
    } catch (err) {
      setError('Erreur lors du chargement des bases de données');
      console.error('Error fetching databases:', err);
      // Mark as restored even on error so loading can be set to false
      hasRestoredRef.current = true;
      return [];
    } finally {
      // Don't set loading to false here - let useEffect handle it after state update
      // This ensures activeDatabase is updated before isLoading becomes false
    }
  }, []);

  // Wrapper for setActiveDatabase that persists to localStorage
  const setActiveDatabase = useCallback((db: Database | null) => {
    setActiveDatabaseState(db);
    if (db) {
      localStorage.setItem(STORAGE_KEY, db.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Mark as restored so we don't restore again after user manually changes selection
    hasRestoredRef.current = true;
  }, []);

  useEffect(() => {
    // Wait for auth check to complete, then fetch databases if authenticated
    if (!isAuthChecking && isAuthenticated) {
      fetchDatabases();
    }
  }, [fetchDatabases, isAuthenticated, isAuthChecking]);

  // Set loading to false after restoration attempt completes
  // This ensures ProtectedRoute doesn't check activeDatabase before restoration completes
  useEffect(() => {
    // Only set loading to false if we've attempted restoration
    // The effect will run when databases.length changes (after fetchDatabases completes)
    // or when activeDatabase changes (after restoration)
    if (isLoading && hasRestoredRef.current) {
      // After restoration attempt, set loading to false
      // This will trigger after the state update from setActiveDatabaseState completes
      // Use setTimeout to ensure this happens after React processes the state updates
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isLoading, activeDatabase, databases.length]);


  return (
    <DatabaseContext.Provider
      value={{
        databases,
        activeDatabase,
        setActiveDatabase,
        isLoading,
        error,
        fetchDatabases,
      }}
    >
      {children}
    </DatabaseContext.Provider>
  );
};

// Export function to clear persisted database (useful for logout)
export const clearPersistedDatabase = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export const useDatabaseContext = () => {
  const context = useContext(DatabaseContext);
  if (context === undefined) {
    throw new Error('useDatabaseContext must be used within a DatabaseProvider');
  }
  return context;
};
